import {
  FILTROS_EXCLUIDOS_PADRAO,
  ORDENACAO_DIRECAO_KEY,
  ORDENACAO_ORDENAR_POR_KEY
} from './listagemConfiguracaoConstants';

export const defaultNormalizeFilterValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
    return value.value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => defaultNormalizeFilterValue(item));
  }

  return value;
};

const isEmptyValue = (value) => {
  if (value === undefined || value === null) {
    return true;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  return false;
};

const buildFilterMaps = (filters) => {
  const filterIds = new Set((filters || []).map((filter) => filter.id));
  const filterById = new Map((filters || []).map((filter) => [filter.id, filter]));

  return { filterIds, filterById };
};

const resolveNormalizeFilterValue = (listagemConfig) => {
  if (typeof listagemConfig?.normalizeFilterValue === 'function') {
    return listagemConfig.normalizeFilterValue;
  }

  return defaultNormalizeFilterValue;
};

const mapValuesToSearchSelectOptions = (values, options, normalizeFilterValue) => {
  const normalizedValues = Array.isArray(values)
    ? values.map((item) => normalizeFilterValue(item))
    : [normalizeFilterValue(values)];

  return normalizedValues
    .filter((value) => !isEmptyValue(value))
    .map((value) => options.find((option) => String(option.value) === String(value)))
    .filter(Boolean);
};

const enrichFilterValueForPanel = (filterId, value, filterById, normalizeFilterValue) => {
  const filter = filterById.get(filterId);
  if (!filter) {
    return value;
  }

  if (filter.type === 'searchSelect') {
    const options = Array.isArray(filter.options) ? filter.options : [];

    if (filter.isMulti) {
      const mapped = mapValuesToSearchSelectOptions(value, options, normalizeFilterValue);
      return mapped.length > 0 ? mapped : value;
    }

    const mapped = mapValuesToSearchSelectOptions([value], options, normalizeFilterValue);
    return mapped[0] || value;
  }

  return normalizeFilterValue(value);
};

// Multi-selecao com valor padrao: limpar precisa ser persistido como [], senao
// o defaultValue volta ao reaplicar a configuracao (filtros iniciais + salvos).
const devePersistirLimpeza = (filter) => (
  filter?.isMulti === true
  && Array.isArray(filter.defaultValue)
  && filter.defaultValue.length > 0
);

export const buildFiltrosParaPersistencia = (values, filters, listagemConfig) => {
  const { filterIds, filterById } = buildFilterMaps(filters);
  const normalizeFilterValue = resolveNormalizeFilterValue(listagemConfig);
  const resultado = {};

  Object.entries(values || {}).forEach(([chave, valor]) => {
    if (FILTROS_EXCLUIDOS_PADRAO.includes(chave)) {
      return;
    }

    if (chave === ORDENACAO_ORDENAR_POR_KEY || chave === ORDENACAO_DIRECAO_KEY) {
      return;
    }

    if (!filterIds.has(chave)) {
      return;
    }

    const valorNormalizado = normalizeFilterValue(valor);

    if (isEmptyValue(valorNormalizado)) {
      if (devePersistirLimpeza(filterById.get(chave))) {
        resultado[chave] = [];
      }
      return;
    }

    resultado[chave] = valorNormalizado;
  });

  return resultado;
};

export const buildOrdenacaoParaPersistencia = (values) => {
  const ordenarPor = values?.[ORDENACAO_ORDENAR_POR_KEY];
  const direcao = values?.[ORDENACAO_DIRECAO_KEY];
  const normalizeFilterValue = defaultNormalizeFilterValue;

  const ordenarPorValido = Array.isArray(ordenarPor)
    ? ordenarPor
      .map((item) => normalizeFilterValue(item))
      .filter((item) => !isEmptyValue(item))
    : [];

  if (ordenarPorValido.length === 0 && isEmptyValue(direcao)) {
    return null;
  }

  return {
    ordenarPor: ordenarPorValido,
    direcao: isEmptyValue(direcao) ? 'asc' : String(direcao)
  };
};

export const buildColunasParaPersistencia = ({
  listagemConfig,
  visibleColumnIds,
  columnVisibilityOverride,
  columnLayoutSnapshot
}) => {
  const todasColunasIds = listagemConfig?.colunas?.todasIds || [];
  const colunasPadraoVisiveis = listagemConfig?.colunas?.padraoVisiveis || [];
  const colunasSempreVisiveis = listagemConfig?.colunas?.sempreVisiveis || [];

  // Fonte da verdade e o estado vivo da tabela (columnLayoutSnapshot) sobre a
  // configuracao aplicada (override). O localStorage do "Salvar layout" NAO
  // participa: com configuracoes habilitadas ele e ignorado (fonte unica).
  const visibility = {
    ...Object.fromEntries(
      todasColunasIds.map((columnId) => [
        columnId,
        colunasPadraoVisiveis.includes(columnId)
      ])
    ),
    ...(columnVisibilityOverride || {}),
    ...(columnLayoutSnapshot?.columnVisibility || {})
  };

  const tableColumnIds = columnLayoutSnapshot?.columnIds;
  if (Array.isArray(tableColumnIds) && tableColumnIds.length > 0) {
    todasColunasIds.forEach((columnId) => {
      if (!tableColumnIds.includes(columnId)) {
        visibility[columnId] = false;
      }
    });
  }

  const order = (columnLayoutSnapshot?.columnOrder || [])
    .filter((columnId) => todasColunasIds.includes(columnId));
  const sizing = {
    ...(columnLayoutSnapshot?.columnSizing || {})
  };

  const orderedIds = order.length > 0
    ? order
    : (Array.isArray(visibleColumnIds) && visibleColumnIds.length > 0
      ? visibleColumnIds
      : colunasPadraoVisiveis);

  const uniqueOrderedIds = [...new Set([
    ...colunasSempreVisiveis,
    ...orderedIds,
    ...todasColunasIds
  ])].filter((columnId) => todasColunasIds.includes(columnId));

  return uniqueOrderedIds.map((columnId, index) => {
    const coluna = {
      id: columnId,
      visivel: visibility[columnId] !== false,
      ordem: index
    };

    if (sizing[columnId]) {
      coluna.largura = sizing[columnId];
    }

    return coluna;
  });
};

const normalizeConfigArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
};

export const mapConfiguracaoParaEstadoListagem = (config, filters, listagemConfig) => {
  const { filterIds, filterById } = buildFilterMaps(filters);
  const normalizeFilterValue = resolveNormalizeFilterValue(listagemConfig);
  const todasColunasIds = listagemConfig?.colunas?.todasIds || [];
  const colunasSempreVisiveis = listagemConfig?.colunas?.sempreVisiveis || [];

  const filtrosConfig = config?.filtros && typeof config.filtros === 'object' ? config.filtros : {};
  const colunasConfig = normalizeConfigArray(config?.colunas);
  const ordenacao = config?.ordenacao && typeof config.ordenacao === 'object' ? config.ordenacao : null;

  const filtrosConhecidos = Object.fromEntries(
    Object.entries(filtrosConfig)
      .filter(([chave]) => filterIds.has(chave))
      .map(([chave, valor]) => [
        chave,
        enrichFilterValueForPanel(chave, valor, filterById, normalizeFilterValue)
      ])
  );

  const filtros = {
    ...filtrosConhecidos
  };

  if (ordenacao?.ordenarPor) {
    filtros[ORDENACAO_ORDENAR_POR_KEY] = enrichFilterValueForPanel(
      ORDENACAO_ORDENAR_POR_KEY,
      ordenacao.ordenarPor,
      filterById,
      normalizeFilterValue
    );
  }

  if (ordenacao?.direcao) {
    filtros[ORDENACAO_DIRECAO_KEY] = ordenacao.direcao;
  }

  const colunasValidas = colunasConfig
    .filter((coluna) => coluna && todasColunasIds.includes(coluna.id))
    .sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0));

  const visibleColumnIds = colunasValidas
    .filter((coluna) => coluna.visivel !== false)
    .map((coluna) => coluna.id);

  const visibleComFixas = [...new Set([
    ...colunasSempreVisiveis,
    ...visibleColumnIds
  ])].filter((columnId) => todasColunasIds.includes(columnId));

  const columnVisibility = colunasValidas.reduce((acc, coluna) => {
    acc[coluna.id] = coluna.visivel !== false;
    return acc;
  }, {});

  colunasSempreVisiveis.forEach((columnId) => {
    columnVisibility[columnId] = true;
  });

  return {
    filtros,
    visibleColumnIds: visibleComFixas.length > 0 ? visibleComFixas : undefined,
    columnVisibility
  };
};

export const extractConfiguracaoListagemError = (error) => {
  const data = error?.response?.data;

  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (data?.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors)
      .flat()
      .filter((message) => typeof message === 'string' && message.trim());

    if (messages.length > 0) {
      return messages.join(' ');
    }
  }

  if (typeof data?.title === 'string' && data.title.trim()) {
    return data.title;
  }

  return error?.message || 'Nao foi possivel concluir a operacao.';
};
